import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/**
 * Die **Liegezeit** (CONTEXT.md) — zwei Sekunden.
 *
 * Sie ist der **Mausweg**: der zweite Klick an derselben Stelle, solange die
 * Zeile noch dasteht. `⌘Z` hängt nicht daran — es hat seit ADR 0028 keine
 * Frist mehr. Die beiden müssen nicht gleich lang sein, sie tun nicht dasselbe.
 */
const GRACE_MS = 2000

/**
 * Die **Liegezeit**: Merkt sich die Aufgaben, die auf DIESER Seite gerade
 * abgehakt wurden. Solange eine Kennung in `graceIds` liegt, zeigt die Seite
 * die erledigte Zeile weiter an ihrem Platz — man sieht den Haken fallen, und
 * das Zurücknehmen ist derselbe Klick an derselben Stelle. Danach fällt sie
 * hinter den **Geschafft-Schalter** (bzw. auf **Demnächst** ganz aus der Liste).
 *
 * **Sie hängt am Klick, nicht an den Daten.** Nur wer hier und jetzt abhakt,
 * bekommt sie: Eine Aufgabe, die beim Aufschlagen der Seite schon erledigt war
 * oder gerade per Sync vom anderen Gerät hereinkommt, liegt nicht erst zwei
 * Sekunden in der Liste herum.
 *
 * Der Filter, den die Seiten damit fahren, lautet überall gleich:
 *
 * ```ts
 * if (task.status === 'completed' && !graceIds.has(task.id)) return false
 * ```
 *
 * `hold` hält die Liegezeit an, solange eine Pointer-Geste läuft: Eine Zeile, die
 * mitten in einem Zug aus dem `SortableContext` fiele, risse dnd-kit das
 * Element unter der Hand weg. Was vorher schon verborgen war, bleibt verborgen
 * — der Schalter flackert nicht.
 *
 * @example
 * const { graceIds, markCompleted } = useCompletionGrace({ hold: !!activeDragId })
 * const toggle = (ids: Set<string>) => { markCompleted(ids); handleToggleComplete(ids) }
 */
export function useCompletionGrace({ hold = false }: { hold?: boolean } = {}): {
  graceIds: Set<string>
  markCompleted: (ids: string | Iterable<string>) => void
} {
  const [graceIds, setGraceIds] = useState<Set<string>>(() => new Set())
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  const markCompleted = useCallback((ids: string | Iterable<string>) => {
    // Die `Leertaste` hakt die ganze Auswahl ab (useTaskListInteraction),
    // der Klick auf das Kästchen genau eine — beide Wege landen hier.
    const list = typeof ids === 'string' ? [ids] : [...ids]
    if (list.length === 0) return

    for (const taskId of list) {
      const existing = timersRef.current.get(taskId)
      if (existing) clearTimeout(existing)
    }

    setGraceIds(prev => {
      const missing = list.filter(id => !prev.has(id))
      if (missing.length === 0) return prev
      const next = new Set(prev)
      for (const id of missing) next.add(id)
      return next
    })

    for (const taskId of list) {
      const timer = setTimeout(() => {
        timersRef.current.delete(taskId)
        setGraceIds(prev => {
          if (!prev.has(taskId)) return prev
          const next = new Set(prev)
          next.delete(taskId)
          return next
        })
      }, GRACE_MS)
      timersRef.current.set(taskId, timer)
    }
  }, [])

  useEffect(() => {
    const timers = timersRef.current
    return () => {
      timers.forEach(t => clearTimeout(t))
      timers.clear()
    }
  }, [])

  // Während einer Geste eingefroren: Die Zeitgeber laufen weiter, aber was
  // beim Griff noch lag, bleibt liegen, bis losgelassen wird.
  const heldRef = useRef<Set<string> | null>(null)
  if (hold && heldRef.current === null) heldRef.current = new Set(graceIds)
  if (!hold && heldRef.current !== null) heldRef.current = null

  const effectiveGraceIds = useMemo(() => {
    const held = heldRef.current
    if (!held || held.size === 0) return graceIds
    const merged = new Set(graceIds)
    for (const id of held) merged.add(id)
    return merged
  // `hold` ist der Auslöser: heldRef wechselt mit ihm, ist als Ref aber unsichtbar für die Regel.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graceIds, hold])

  return { graceIds: effectiveGraceIds, markCompleted }
}
